import config from 'config';
import { EMPTY_ARRAY } from 'constants/defaults';
import { BUDGET_CALCULATION, BUDGET_INTERVAL, BUDGET_RECURRENCE_END } from 'constants/project';
import { tagFactory, tagListFactory } from 'factories/projects';
import { isEqual, partition, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { createXhrStatusModule, xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type {
  BudgetRecurrence,
  Draft,
  LightProject,
  Project,
  ProjectUserRelation,
  ProjectVariant,
} from 'types/project';
import { TagSettings } from 'types/project';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { objectNumberKeys, uniqBy } from 'utils/typesafe';
import { decodeUrlParams } from 'utils/url-params';
import * as pinsTypes from './pins.types';
import type { BudgetEntity, State } from './projects.types';
import * as types from './projects.types';

const { withXhrStatusReducer } = createXhrStatusModule('projects');
const urlState = decodeUrlParams(window.location.search);

export const initialState: State = {
  xhrStatus: xhrStatusFactory(),
  draftOriginal: null,
  draft: null,
  projectPermissionsByUserId: {},
  projectById: {},
  projectIds: [],
  projectFetchingStatus: 'lazy',
  paginatedView: null,
  filter: {
    sortBy: {
      field: (urlState.field as types.FilterProjectSortByFields) || 'client_name',
      direction: urlState.direction || 'asc',
    },
    projectStatus: urlState.projectStatus || 'active',
    projectRelation: urlState.projectRelation || 'assigned',
    budgetStatus: urlState.budgetStatus || 'all',
    labelIds: urlState.labelIds || EMPTY_ARRAY,
    clientIds: urlState.clientIds || EMPTY_ARRAY,
    query: urlState.query || '',
  },
  budgets: {
    entities: {},
    intervalsByProjectId: {},
    fetchStatus: {},
  },
};

const budgetRecurrenceReducer = reducerWithInitialState<BudgetRecurrence>({
  recur: BUDGET_INTERVAL.NONE,
  start_date: null,
  end_date: null,
  recur_until: BUDGET_RECURRENCE_END.ARCHIVED,
})
  // Budget recurrence
  .caseWithAction(types.updateDraftBudgetRecurrence, (state, action): BudgetRecurrence => {
    return {
      ...state,
      end_date: null,
      recur_until: BUDGET_RECURRENCE_END.ARCHIVED,
      ...action.payload,
    };
  })
  .caseWithAction(types.updateDraftBudgetRecurrenceStart, (state, action): BudgetRecurrence => {
    return {
      ...state,
      start_date: action.payload,
    };
  })
  .caseWithAction(types.updateDraftBudgetRecurrenceEnd, (state, action): BudgetRecurrence => {
    return {
      ...state,
      end_date: action.payload,
      recur_until: action.payload ? BUDGET_RECURRENCE_END.END_DATE : BUDGET_RECURRENCE_END.ARCHIVED,
    };
  });

function touchProjectDraft(draft: Draft | null): Draft | null {
  if (draft == null) {
    return null;
  }
  return { ...draft, updated_at: DateTime.now().toISO() };
}

const reducer = reducerWithInitialState<State>(initialState)
  .caseWithAction(types.setDraftOriginal, (state, action): State => {
    return { ...state, draftOriginal: action.payload };
  })
  .caseWithAction(types.clearOriginalDraft, (state): State => {
    return { ...state, draftOriginal: null };
  })
  .caseWithAction(types.createDraft, (state, action): State => {
    return { ...state, draft: action.payload };
  })
  .case(types.clearDraft, (state): State => {
    return { ...state, draft: null, draftOriginal: null };
  })
  .caseWithAction(types.updateDraft, (state, action): State => {
    const updatedDraft = action.payload
      ? touchProjectDraft({ ...state.draft, ...action.payload } as Draft)
      : null;

    if (state.draft?.default_labels && updatedDraft?.default_labels === false) {
      updatedDraft.defaultTags = {};
      updatedDraft.labels = updatedDraft.labels.filter(label => !label.default);
    }

    return { ...state, draft: updatedDraft };
  })
  .caseWithAction(types.updateProjectUserAssociation, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    const users = (state.draft.users || []).map((user): ProjectUserRelation => {
      if (user.user_id !== action.payload.userId) {
        return user;
      }
      return { ...user, ...action.payload.changes };
    });
    return { ...state, draft: touchProjectDraft({ ...state.draft, users }) };
  })
  .caseWithAction(types.removeUserIdFromDraft, (state, action): State => {
    if (!state.draft || !state.draft.users) {
      return state;
    }
    const updatedDraft = touchProjectDraft({
      ...state.draft,
      users: state.draft.users.filter(u => u.user_id !== action.payload),
    });
    return {
      ...state,
      draft: updatedDraft,
    };
  })
  .caseWithAction(types.tagListAdd, (state, action): State => {
    if (!state.draft) {
      return state;
    }

    const patch = {
      ...state.draft,
      labels: [
        ...state.draft.labels,
        tagListFactory(action.payload.id, undefined, action.payload.default),
      ],
    };

    if (action.payload.default && state.draft.enable_labels === TagSettings.All) {
      patch.enable_labels = TagSettings.Custom;
    }

    return {
      ...state,
      draft: patch,
    };
  })
  .caseWithAction(types.tagListRemove, (state, action): State => {
    if (!state.draft) {
      return state;
    }

    const { id, children } = action.payload;

    const updatedLabels = state.draft.labels.filter(
      x => x.label_id !== id && !children.includes(x.label_id)
    );

    const patch: Partial<Draft> = {
      labels: updatedLabels,
    };

    if (state.draft.enable_labels !== TagSettings.None) {
      const prevManualLabels = state.draft.labels.filter(tag => !tag.default);
      const [defaultLabels, manualLabels] = partition(updatedLabels, tag => tag.default);

      // If we removed the last manual label, disable labels and required labels
      if (prevManualLabels.length && !manualLabels.length) {
        patch.enable_labels = TagSettings.None;
        patch.required_labels = false;
      }

      // Ensure that we don't select all labels if we have a mix of default and manual labels
      else if (manualLabels.length && defaultLabels.length) {
        patch.enable_labels = TagSettings.Custom;
      }
    }

    return {
      ...state,
      draft: {
        ...state.draft,
        ...patch,
      },
    };
  })
  .caseWithAction(types.tagAdd, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: [...state.draft.labels, tagFactory(action.payload.id)],
      },
    };
  })
  .caseWithAction(types.tagRemove, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: state.draft.labels.filter(x => x.label_id !== action.payload.id),
      },
    };
  })
  .case(types.tagAddBatch, (state, payload): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: uniqBy(
          [
            ...state.draft.labels,
            ...payload.ids.map(id => tagFactory(id, undefined, payload.default)),
          ],
          'label_id'
        ),
      },
    };
  })
  .caseWithAction(types.tagRemoveBatch, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: [...state.draft.labels.filter(label => !action.payload.includes(label.label_id))],
      },
    };
  })
  .caseWithAction(types.tagListChangeRequired, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    const { id, required } = action.payload;
    const required_label_ids = state.draft.required_label_ids ?? [];
    const next = required
      ? uniq(required_label_ids.concat([id]))
      : required_label_ids.filter(labelId => labelId !== id);

    return {
      ...state,
      draft: {
        ...state.draft,
        required_label_ids: next,
      },
    };
  })
  .caseWithAction(types.tagListRemoveBatch, (state): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: state.draft.labels.filter(tag => tag.default),
      },
    };
  })
  .caseWithAction(types.tagChangeBudget, (state, action): State => {
    if (!state.draft) {
      return state;
    }
    const { id, budget } = action.payload;
    return {
      ...state,
      draft: {
        ...state.draft,
        labels: state.draft.labels.map(x => (x.label_id !== id ? x : { ...x, budget })),
      },
    };
  })
  .casesWithAction(
    [
      types.updateDraftBudgetRecurrence,
      types.updateDraftBudgetRecurrenceStart,
      types.updateDraftBudgetRecurrenceEnd,
    ],
    (state, action): State => {
      if (!state.draft) {
        return state;
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          budget_recurrence: budgetRecurrenceReducer(state.draft.budget_recurrence, action),
        },
      };
    }
  )

  .casesWithAction(
    [types.fetchedLightProjects, pinsTypes.fetchedProjectPins],
    (state, action): State => {
      const { projects } = action.payload;
      let newState:
        | { byId: Record<number, ProjectVariant | undefined>; ids: Set<number> }
        | undefined;
      projects.forEach((project: LightProject) => {
        const existingProject = state.projectById[project.id];
        if (existingProject != null) {
          if (existingProject.type === 'full') {
            // Don't overwrite full projects
            return;
          } else if (isEqual(existingProject.lightProject, project)) {
            // Don't update with equal object
            return;
          }
        }

        // Initialize new map if needed
        // If all projects short circuit in logic above, the state will not needlessly update
        if (newState == null) {
          newState = { byId: { ...state.projectById }, ids: new Set(state.projectIds) };
        }
        newState.byId[project.id] = { type: 'light', lightProject: project };
        newState.ids.add(project.id);
      });

      if (!newState) {
        return state;
      }

      return { ...state, projectById: newState.byId, projectIds: Array.from(newState.ids) };
    }
  )
  .caseWithAction(types.fetchedFullProjects, (state, action): State => {
    const projects = action.payload;
    let newState:
      | { byId: Record<number, ProjectVariant | undefined>; ids: Set<number> }
      | undefined;
    const projectsToInclude: Array<Project> = [];
    projects.forEach((project: Project) => {
      let newOrUpdatedProject = project;
      const existingProject = state.projectById[project.id];
      if (existingProject != null) {
        if (existingProject.type === 'full') {
          // Don't update with equal object
          if (isEqual(existingProject.project, project)) return;

          newOrUpdatedProject = { ...existingProject.project, ...project };
          projectsToInclude.push(project);
        }
      }

      // Initialize new map if needed
      // If all projects short circuit in logic above, the state will not needlessly update
      if (newState == null) {
        newState = { byId: { ...state.projectById }, ids: new Set(state.projectIds) };
      }
      newState.byId[project.id] = { type: 'full', project: newOrUpdatedProject };
      newState.ids.add(project.id);
    });

    if (!newState) {
      return state;
    }

    const patch: Partial<State> = {
      projectById: newState.byId,
      projectIds: [...newState.ids.values()],
    };

    // If we're in a paginated view and the projects were updated from a Pusher notification, we
    // should try to put the updated projects into the current page if there's still room.
    if (state.paginatedView) {
      const remainingRoomInPage =
        config.projects.projectsPerPage - state.paginatedView.projectIds.length;
      if (remainingRoomInPage > 0) {
        const projectIdsToInclude = projectsToInclude
          .filter(project => (state.filter.projectStatus === 'archived') !== project.active)
          .slice(0, remainingRoomInPage)
          .map(project => project.id);

        patch.paginatedView = {
          ...state.paginatedView,
          projectIds: uniq(state.paginatedView.projectIds.concat(projectIdsToInclude)),
        };
      }
    }

    return { ...state, ...patch };
  })
  .case(
    types.setProjectFetchingStatus,
    (state, payload): State => ({ ...state, projectFetchingStatus: payload })
  )

  .caseWithAction(types.projectSaved, (state, action): State => {
    const { project, createdEntry } = action.payload;

    return {
      ...state,
      projectById: { ...state.projectById, [project.id]: { type: 'full', project } },
      projectIds: createdEntry ? state.projectIds.concat([project.id]) : state.projectIds,
    };
  })
  .caseWithAction(types.removeProject, (state, action): State => {
    return {
      ...state,
      projectById: { ...state.projectById, [action.payload]: undefined },
      projectIds: state.projectIds.filter(id => id !== action.payload),
    };
  })
  .caseWithAction(types.archiveProject, (state, action): State => {
    const projectById = {
      ...state.projectById,
      [action.payload]: manipulateProjectVariant(
        state.projectById[action.payload],
        light => ({ ...light, active: false }),
        full => ({ ...full, active: false })
      ),
    };
    return {
      ...state,
      projectById,
    };
  })
  .caseWithAction(types.archiveProjectsForClient, (state, action): State => {
    return {
      ...state,
      projectById: { ...state.projectById, ...action.payload },
    };
  })

  .caseWithAction(types.setProjectPermissions, (state, action): State => {
    const { userId, permissions } = action.payload;
    const existing = state.projectPermissionsByUserId[userId] ?? [];

    const merged = uniq(existing.concat(permissions));
    return {
      ...state,
      projectPermissionsByUserId: {
        ...state.projectPermissionsByUserId,
        [userId]: merged,
      },
    };
  })
  .caseWithAction(types.touchProjectPermission, (state, action): State => {
    const { userId, projectId } = action.payload;

    const updated = (state.projectPermissionsByUserId[userId] ?? []).slice();
    const projectIndex = updated.indexOf(projectId);

    if (projectIndex === -1) {
      return state;
    }

    updated.splice(projectIndex, 1);
    updated.unshift(projectId);

    return {
      ...state,
      projectPermissionsByUserId: {
        ...state.projectPermissionsByUserId,
        [userId]: updated,
      },
    };
  })
  .case(types.replaceProjectPermissionForAll, (state, payload): State => {
    const { projectId, userIds = [] } = payload;

    const updatedPermissions = objectNumberKeys(state.projectPermissionsByUserId).reduce<
      Record<number, Array<number>>
    >((updatedPermissions, userId) => {
      const existingPermissions = state.projectPermissionsByUserId[userId] ?? [];
      const userShouldHavePermission = userIds.includes(userId);
      const userAlreadyHasPermission = existingPermissions.some(id => id === projectId);
      if (userShouldHavePermission && userAlreadyHasPermission) {
        // Avoid shuffling order of existing permissions
        updatedPermissions[userId] = existingPermissions;
        return updatedPermissions;
      }

      let updatedPermissionsForUser = existingPermissions;
      if (userShouldHavePermission) {
        updatedPermissionsForUser = updatedPermissionsForUser.concat([projectId]);
      } else {
        updatedPermissionsForUser = existingPermissions.filter(
          permission => permission !== projectId
        );
      }
      updatedPermissions[userId] = updatedPermissionsForUser;
      return updatedPermissions;
    }, {});

    return {
      ...state,
      projectPermissionsByUserId: updatedPermissions,
    };
  })
  .caseWithAction(types.setProjectBudgetAsPending, (state, action): State => {
    const projectById = {
      ...state.projectById,
      [action.payload]: manipulateProjectVariant(
        state.projectById[action.payload],
        light => ({ ...light, budget_calculation: BUDGET_CALCULATION.PENDING }),
        full => ({ ...full, budget_calculation: BUDGET_CALCULATION.PENDING })
      ),
    };
    return {
      ...state,
      projectById,
    };
  })
  .caseWithAction(types.setFilter, (state, action): State => {
    return {
      ...state,
      filter: action.payload,
    };
  })

  .caseWithAction(types.fetchedBudgets, (state, action): State => {
    const entities = budgetEntitiesReducerFactory(state.budgets.entities, action.payload);
    return {
      ...state,
      budgets: {
        ...state.budgets,
        entities,
        intervalsByProjectId: {
          ...state.budgets.intervalsByProjectId,
          [action.payload.projectId]: Object.keys(entities[action.payload.projectId]),
        },
      },
    };
  })

  .caseWithAction(types.setPaginatedProjectView, (state, action): State => {
    return {
      ...state,
      paginatedView: action.payload,
    };
  })
  .case(types.tagListChangeDefault, (state, payload): State => {
    if (!state.draft) {
      return state;
    }

    const defaultTags = Object.assign({}, state.draft.defaultTags, {
      [payload.tagListId]: payload.tagId,
    });

    return {
      ...state,
      draft: {
        ...state.draft,
        defaultTags,
      },
    };
  });

export const manipulateProjectVariant = (
  variant: ProjectVariant | undefined,
  manipulateLight: (lightProject: LightProject) => LightProject,
  manipulateFull: (project: Project) => Project
): ProjectVariant | undefined => {
  if (variant == null) {
    return undefined;
  }
  if (variant.type === 'full') {
    return { type: 'full', project: manipulateFull(variant.project) };
  }
  return { type: 'light', lightProject: manipulateLight(variant.lightProject) };
};

/**
 * Budget stuff only relevant on ProjectDetailsBudgetRoute.js
 */
const budgetEntriesReducerFactory = (
  state: Record<number, BudgetEntity> = {},
  entries: Array<BudgetEntity>
): Record<string, BudgetEntity> => {
  return entries.reduce<Record<string, BudgetEntity>>(
    (acc, budget) => {
      acc[budget.recur] = { ...budget, key: budget.recur };
      return acc;
    },
    { ...state }
  );
};

const budgetEntitiesReducerFactory = (
  state: Record<number, Record<number, BudgetEntity>>,
  payload: { budgets: Array<BudgetEntity>; projectId: number }
) => {
  return {
    ...state,
    [payload.projectId]: budgetEntriesReducerFactory(state[payload.projectId], payload.budgets),
  };
};

export default withXhrStatusReducer(reducer);
